import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function PagerToolbar({ pageLabel, onPrev, onNext, disablePrev, disableNext, fitMode, onFitModeChange, variant }) {
  const compact = variant === "overlay";
  return (
    <div
      className={`lread-flip-toolbar${compact ? " lread-flip-toolbar--overlay" : ""}`}
      role="toolbar"
      aria-label="PDF reader controls"
    >
      <button type="button" className="lread-flip-btn" disabled={disablePrev} onClick={onPrev}>
        ← Prev
      </button>
      <span className="lread-flip-toolbar__meta">{pageLabel}</span>
      <button type="button" className="lread-flip-btn" disabled={disableNext} onClick={onNext}>
        Next →
      </button>
      <span className="lread-flip-toolbar__sep" aria-hidden />
      <div className="lread-pdf-fit" role="group" aria-label="Fit mode">
        <button
          type="button"
          className={`lread-flip-btn lread-flip-btn--ghost${fitMode === "fit_page" ? " is-active" : ""}`}
          onClick={() => onFitModeChange("fit_page")}
        >
          Fit page
        </button>
        <button
          type="button"
          className={`lread-flip-btn lread-flip-btn--ghost${fitMode === "fit_width" ? " is-active" : ""}`}
          onClick={() => onFitModeChange("fit_width")}
        >
          Fit width
        </button>
      </div>
    </div>
  );
}

/**
 * PDF reader: fit-page by default (scale from natural viewport vs container), optional fit-width + scroll.
 */
export default function LibraryComicPdfPager({
  pdfUrl = null,
  title = "Comic",
  onPageChange,
  variant = "inline",
  initialPageIndex = 0,
}) {
  const stageRef = useRef(null);
  const naturalRef = useRef({ w: 0, h: 0 });
  const numPagesRef = useRef(0);
  const initialPageIndexRef = useRef(Math.max(0, Math.floor(Number(initialPageIndex) || 0)));
  initialPageIndexRef.current = Math.max(0, Math.floor(Number(initialPageIndex) || 0));
  const [box, setBox] = useState({ w: 400, h: 520 });
  const [pageIndex, setPageIndex] = useState(() => Math.max(0, Math.floor(Number(initialPageIndex) || 0)));
  const [numPages, setNumPages] = useState(null);
  const [pdfError, setPdfError] = useState("");
  const [fitScale, setFitScale] = useState(1);
  const [fitMode, setFitMode] = useState("fit_page");

  const recomputeScale = useCallback(() => {
    const pw = naturalRef.current.w;
    const ph = naturalRef.current.h;
    if (!pw || !ph || !box.w) return;
    const padX = 12;
    const padY = 12;
    const cw = Math.max(80, box.w - padX * 2);
    let ch = Math.max(0, box.h - padY * 2);
    const overlay = variant === "overlay";
    if (!ch || ch < 120) {
      const vh = typeof window !== "undefined" ? window.innerHeight : 800;
      ch = overlay ? Math.max(360, Math.floor(vh * 0.72)) : Math.max(240, Math.floor(vh * 0.42));
    }
    let s;
    if (fitMode === "fit_width") {
      s = (cw / pw) * 0.98;
    } else {
      s = Math.min(cw / pw, ch / ph) * 0.96;
    }
    setFitScale(Math.max(0.12, Math.min(s, 8)));
  }, [box.w, box.h, fitMode, variant]);

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;

    const apply = () => {
      const r = el.getBoundingClientRect();
      const w = Math.max(100, Math.floor(r.width));
      let h = Math.floor(r.height);
      if (variant === "overlay" && (!Number.isFinite(h) || h < 160)) {
        const vh = typeof window !== "undefined" ? window.innerHeight : 800;
        h = Math.max(360, Math.floor(vh * 0.72));
      } else {
        h = Math.max(100, h);
      }
      setBox({ w, h });
    };

    apply();
    const ro = new ResizeObserver(() => {
      apply();
      requestAnimationFrame(apply);
    });
    ro.observe(el);
    window.addEventListener("resize", apply);
    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(apply);
    });
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, [variant]);

  useEffect(() => {
    recomputeScale();
  }, [recomputeScale]);

  useLayoutEffect(() => {
    const id = requestAnimationFrame(() => recomputeScale());
    return () => cancelAnimationFrame(id);
  }, [fitMode, recomputeScale]);

  useEffect(() => {
    numPagesRef.current = numPages || 0;
  }, [numPages]);

  useEffect(() => {
    setPdfError("");
    naturalRef.current = { w: 0, h: 0 };
    setNumPages(null);
    setPageIndex(initialPageIndexRef.current);
  }, [pdfUrl]);

  const total = numPages || 0;
  useEffect(() => {
    if (!numPages) return;
    setPageIndex((i) => Math.min(Math.max(0, i), numPages - 1));
  }, [numPages]);

  const goPrev = useCallback(() => {
    setPageIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNext = useCallback(() => {
    setPageIndex((i) => {
      const max = Math.max(0, numPagesRef.current - 1);
      return Math.min(max, i + 1);
    });
  }, []);

  const idx = total > 0 ? Math.min(pageIndex, total - 1) : 0;
  const pageLabel =
    total > 0 ? `Page ${idx + 1} of ${total}` : pdfError ? "Error" : numPages === null ? "Loading…" : "—";

  useEffect(() => {
    if (typeof onPageChange !== "function" || total <= 0) return;
    onPageChange({ index: idx, pageNumber: idx + 1, total });
  }, [idx, total, onPageChange]);

  const onPageLoadSuccess = useCallback(
    (page) => {
      const vp = page.getViewport({ scale: 1 });
      naturalRef.current = { w: vp.width, h: vp.height };
      recomputeScale();
    },
    [recomputeScale]
  );

  useEffect(() => {
    if (!pdfUrl || !numPages) return undefined;
    const onKey = (e) => {
      if (e.defaultPrevented) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pdfUrl, numPages, goPrev, goNext]);

  if (!pdfUrl) {
    return (
      <div className="lread-flipbook">
        <p className="lread-flipbook__error">No readable PDF URL was issued for this edition.</p>
      </div>
    );
  }

  const overlay = variant === "overlay";

  return (
    <div className={`lread-pdf-pager${overlay ? " lread-pdf-pager--overlay" : ""}`} tabIndex={-1}>
      {!overlay ? (
        <p className="lread-flipbook__hint">
          PDF — Fit page by default. {title}{" "}
          <a className="lread-flipbook__link" href={pdfUrl} target="_blank" rel="noopener noreferrer">
            Open PDF in a new tab
          </a>
          .
        </p>
      ) : null}
      {pdfError ? (
        <div className="lread-flipbook__error">
          <p>{pdfError}</p>
          <a className="lread-flipbook__link" href={pdfUrl} target="_blank" rel="noopener noreferrer">
            Open PDF
          </a>
        </div>
      ) : (
        <Document
          file={pdfUrl}
          loading={<p className="lread-flipbook__loading">Loading comic…</p>}
          onLoadSuccess={({ numPages: n }) => {
            setPdfError("");
            setNumPages(n);
          }}
          onLoadError={(err) => {
            setPdfError(err?.message || "Could not load PDF.");
            setNumPages(null);
          }}
        >
          <div className={`lread-pdf-pager__column${overlay ? " lread-pdf-pager__column--overlay" : ""}`}>
            <div
              ref={stageRef}
              className={`lread-pdf-pager__stage${fitMode === "fit_width" ? " lread-pdf-pager__stage--scroll" : ""}`}
            >
              {numPages ? (
                <Page
                  key={`pg-${idx}`}
                  pageNumber={idx + 1}
                  scale={fitScale}
                  onLoadSuccess={onPageLoadSuccess}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              ) : (
                <p className="lread-flipbook__loading">Preparing pages…</p>
              )}
            </div>
            {numPages ? (
              <PagerToolbar
                pageLabel={pageLabel}
                onPrev={goPrev}
                onNext={goNext}
                disablePrev={total <= 0 || idx <= 0}
                disableNext={total <= 0 || idx >= total - 1}
                fitMode={fitMode}
                onFitModeChange={setFitMode}
                variant={variant}
              />
            ) : null}
          </div>
        </Document>
      )}
    </div>
  );
}
